import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Search, Heart, ChevronLeft, Filter as FilterIcon, X } from "lucide-react";

const PROTEINS = ["All", "Beef", "Eggs", "Fish", "Chicken"];
const OCCASIONS = ["All", "Breakfast", "Brunch", "Lunch", "Dinner", "Snack"];
const CARBS = ["All", "Lower", "Moderate", "Higher"];
const TAGS = ["Quick", "Family-friendly", "Batch cook", "One-pan", "No-cook", "Budget", "Leftovers"];

const PROTEIN_COLOR = {
  Beef: "bg-red-600",
  Eggs: "bg-amber-500",
  Fish: "bg-sky-600",
  Chicken: "bg-orange-500",
};

export default function EatMeals() {
  const [params, setParams] = useSearchParams();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMore, setShowMore] = useState(false);

  const protein = params.get("protein") || "All";
  const occasion = params.get("occasion") || "All";
  const carb = params.get("carb") || "All";
  const tag = params.get("tag") || "";
  const search = params.get("search") || "";

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "All") p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (protein !== "All") q.set("protein", protein);
        if (occasion !== "All") q.set("occasion", occasion);
        if (carb !== "All") q.set("carb", carb);
        if (tag) q.set("tag", tag);
        if (search) q.set("search", search);
        const r = await api.get(`/eat/meals${q.toString() ? "?" + q.toString() : ""}`);
        setMeals(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [protein, occasion, carb, tag, search]);

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });
  const anyFilter = protein !== "All" || occasion !== "All" || carb !== "All" || tag || search;

  const toggleFav = async (mealId, isFav) => {
    try {
      await api.post(`/eat/favourites/${mealId}`);
      setMeals((prev) => prev.map((m) => m.id === mealId ? { ...m, is_favourite: !isFav } : m));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="eat-meals-page">
      <div>
        <Link to="/app/eat" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary" data-testid="back-to-eat">
          <ChevronLeft size={14} /> Eat
        </Link>
        <div className="label-eyebrow mt-3">Meal Library</div>
        <h1 className="wordmark mt-2 text-4xl md:text-5xl uppercase">All Meals.</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">Browse the full 40-meal library. Filter by protein, meal time and lifestyle. Search by ingredient.</p>
      </div>

      {/* Search + primary filters */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setParam("search", e.target.value)}
            placeholder="Search meal name or ingredient…"
            className="w-full pl-9 pr-9 py-2.5 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="meals-search"
          />
          {search && (
            <button onClick={() => setParam("search", "")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="meals-search-clear">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Protein</div>
            <div className="flex flex-wrap gap-1.5">
              {PROTEINS.map((p) => (
                <button key={p} onClick={() => setParam("protein", p)}
                  className={`px-3 py-1.5 rounded-md text-xs font-head uppercase tracking-wider border transition-colors ${protein === p ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                  data-testid={`filter-protein-${p.toLowerCase()}`}>{p}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Meal Time</div>
            <div className="flex flex-wrap gap-1.5">
              {OCCASIONS.map((o) => (
                <button key={o} onClick={() => setParam("occasion", o)}
                  className={`px-3 py-1.5 rounded-md text-xs font-head uppercase tracking-wider border transition-colors ${occasion === o ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                  data-testid={`filter-occasion-${o.toLowerCase()}`}>{o}</button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={() => setShowMore((v) => !v)} className="text-xs uppercase tracking-widest text-primary inline-flex items-center gap-1 hover:underline" data-testid="meals-more-filters">
          <FilterIcon size={12} /> {showMore ? "Hide" : "More"} Filters
        </button>

        {showMore && (
          <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-border">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Carb Profile</div>
              <div className="flex flex-wrap gap-1.5">
                {CARBS.map((c) => (
                  <button key={c} onClick={() => setParam("carb", c)}
                    className={`px-3 py-1.5 rounded-md text-xs font-head uppercase tracking-wider border transition-colors ${carb === c ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                    data-testid={`filter-carb-${c.toLowerCase()}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Lifestyle</div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setParam("tag", "")}
                  className={`px-3 py-1.5 rounded-md text-xs font-head uppercase tracking-wider border transition-colors ${!tag ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}>Any</button>
                {TAGS.map((t) => (
                  <button key={t} onClick={() => setParam("tag", t)}
                    className={`px-3 py-1.5 rounded-md text-xs font-head uppercase tracking-wider border transition-colors ${tag === t ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                    data-testid={`filter-tag-${t.toLowerCase().replace(/[^a-z]/g, "")}`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {anyFilter && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">{meals.length} meal{meals.length === 1 ? "" : "s"} match</div>
            <button onClick={clearAll} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="meals-clear-filters">Clear all</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading meals…</div>
      ) : meals.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <div className="font-head uppercase text-lg">No meals match those filters.</div>
          <button onClick={clearAll} className="btn-secondary btn-sm mt-4">Clear filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meals.map((m) => (
            <Link key={m.id} to={`/app/eat/meals/${m.slug}`} className="group bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors" data-testid={`meal-card-${m.slug}`}>
              <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                {m.image && <img src={m.image} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <div className="absolute top-3 left-3">
                  <span className={`${PROTEIN_COLOR[m.primary_protein] || "bg-foreground"} text-white text-[10px] font-head uppercase tracking-widest px-2 py-1 rounded-md`}>{m.primary_protein}</span>
                </div>
                <button
                  onClick={(e) => { e.preventDefault(); toggleFav(m.id, m.is_favourite); }}
                  className={`absolute top-3 right-3 w-8 h-8 rounded-md flex items-center justify-center transition-colors ${m.is_favourite ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground/70 hover:text-primary"}`}
                  data-testid={`meal-fav-${m.slug}`}
                  aria-label={m.is_favourite ? "Remove favourite" : "Add favourite"}
                >
                  <Heart size={14} strokeWidth={2.5} fill={m.is_favourite ? "currentColor" : "none"} />
                </button>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.meal_occasion}</div>
                <div className="mt-1 font-head uppercase text-lg leading-tight">{m.name}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(m.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest bg-muted text-foreground/70 px-2 py-0.5 rounded">{t}</span>
                  ))}
                  <span className="text-[10px] uppercase tracking-widest border border-primary/40 text-primary px-2 py-0.5 rounded">{m.carb_profile} Carbs</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
